import React, { memo, useId, useState } from 'react';
import classNames from 'classnames';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import Typography from '@lib/components/Typography/Typography';
import styles from './Accordion.module.scss';

interface Props {
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
  'data-test-id'?: string;
  defaultOpen?: boolean;
  // Reports the new open state. The accordion keeps owning it — this is for
  // callers whose content is too expensive to mount while it is folded away.
  onToggle?: (isOpen: boolean) => void;
  title: React.ReactNode;
  toggleClassName?: string;
}

function Accordion(props: Props) {
  const {
    children,
    className,
    contentClassName,
    'data-test-id': dataTestId,
    defaultOpen = true,
    onToggle,
    title,
    toggleClassName,
  } = props;
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const panelId = useId();
  const labelId = useId();

  return (
    <div className={className}>
      <button
        id={labelId}
        type="button"
        className={classNames(
          styles.toggle,
          { [styles.toggleOpen]: isOpen },
          toggleClassName,
        )}
        onClick={() => {
          // Derived outside the updater: StrictMode invokes updaters twice, and
          // `onToggle` is a side effect that must fire once per click.
          const nextIsOpen = !isOpen;
          setIsOpen(nextIsOpen);
          onToggle?.(nextIsOpen);
        }}
        aria-expanded={isOpen}
        aria-controls={panelId}
        data-test-id={dataTestId}
      >
        <div className={styles.title}>
          {typeof title === 'string' ? (
            <Typography variant="body" strong>
              {title}
            </Typography>
          ) : (
            title
          )}
        </div>
        <MaterialIcon icon="arrow_drop_down" className={styles.icon} />
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={labelId}
        className={classNames(styles.panel, { [styles.panelOpen]: isOpen })}
        inert={!isOpen}
      >
        <div className={styles.panelInner}>
          <div className={classNames(styles.content, contentClassName)}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

export default memo(Accordion);
